import { useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  getSessionItem, 
  clearSession, 
  isSessionExpiredDueToInactivity,
  initActivityTracking,
  updateLastActivity,
  getCurrentUser,
  getSessionToken
} from '../utils/sessionManager';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Session validation interval (check every 2 minutes - balanced between detection and performance)
const SESSION_CHECK_INTERVAL = 120000;

export const useSessionValidation = () => {
  const isValidatingRef = useRef(false);
  const hasExpiredRef = useRef(false);
  const intervalIdRef = useRef(null);
  
  const handleSessionExpired = useCallback((reason) => {
    // Prevent multiple expiry handling
    if (hasExpiredRef.current) return;
    hasExpiredRef.current = true;
    
    // Clear the interval to stop further checks
    if (intervalIdRef.current) {
      clearInterval(intervalIdRef.current);
      intervalIdRef.current = null;
    }
    
    // Clear all session data
    clearSession();
    
    // Show toast message with longer duration
    toast.error(reason || 'Your session has expired. Please login again.', {
      duration: 10000,
      style: {
        background: '#ef4444',
        color: 'white',
        fontWeight: 'bold'
      }
    });
    
    // Redirect to login after a short delay
    setTimeout(() => {
      window.location.href = '/login';
    }, 2000);
  }, []);
  
  const validateSession = useCallback(async () => {
    // Prevent concurrent validations
    if (isValidatingRef.current || hasExpiredRef.current) return;
    
    const userLoggedIn = getSessionItem('userLoggedIn');
    const userType = getSessionItem('userType');
    
    // Only validate for regular users (not vendors)
    if (!userLoggedIn || userType === 'vendor') return;
    
    // Check for inactivity timeout first
    if (isSessionExpiredDueToInactivity()) {
      handleSessionExpired('Your session has expired due to inactivity. Please login again.');
      return;
    }
    
    const user = getCurrentUser();
    const sessionToken = getSessionToken();
    
    if (!user?.id || !sessionToken) return;
    
    isValidatingRef.current = true;
    
    try {
      const response = await axios.post(`${API}/auth/validate-session`, {
        user_id: user.id,
        session_token: sessionToken
      });
      
      if (!response.data.valid) {
        // Session is invalid - user logged in from another device
        handleSessionExpired(response.data.reason);
      }
    } catch (error) {
      console.error('[SessionValidation] Error:', error.message);
      // Don't logout on network errors, only on explicit invalid session
    } finally {
      isValidatingRef.current = false;
    }
  }, [handleSessionExpired]);
  
  useEffect(() => {
    // Initialize activity tracking for inactivity timeout
    const cleanupActivityTracking = initActivityTracking(handleSessionExpired);
    
    // Initial validation
    validateSession();
    
    // Set up interval for periodic validation
    intervalIdRef.current = setInterval(validateSession, SESSION_CHECK_INTERVAL);
    
    // Also validate when tab becomes visible (user switches back to this tab)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        validateSession();
      }
    };
    
    // Also validate on window focus
    const handleFocus = () => {
      validateSession();
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    
    // Cleanup on unmount
    return () => {
      cleanupActivityTracking();
      if (intervalIdRef.current) {
        clearInterval(intervalIdRef.current);
        intervalIdRef.current = null;
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [validateSession, handleSessionExpired]);
  
  return { validateSession };
};

export default useSessionValidation;
